import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { IZonaCiudad } from './interfaces/zona-ciudad.interface';
import { ZonaCiudadService } from './services/zona-ciudad.service';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';
import { ZonaCiudadListComponent } from './zona-ciudad-list.component';

@Component({
  selector: 'app-zona-ciudad-form',
  templateUrl: './zona-ciudad-form.component.html',
  providers: [MessageService]
})
export class ZonaCiudadFormComponent implements OnInit, OnChanges {
  @Input() zonaCiudad: IZonaCiudad | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();

  form!: FormGroup;
  loading: boolean = false;
  displayCiudadesDialog: boolean = false;
  selectedZona: string | null = null;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private zonaCiudadService: ZonaCiudadService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.resetForm();
    if (this.zonaCiudad) {
      this.loadZonaCiudad();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['zonaCiudad']) {
      this.resetForm();
      if (changes['zonaCiudad'].currentValue) {
        this.loadZonaCiudad();
      }
    }
  }

  resetForm(): void {
    this.form = this.fb.group({
      zona: [{ value: '', disabled: !!this.zonaCiudad }, [Validators.required]],
      descripcion: ['', [Validators.required]]
    });
  }

  loadZonaCiudad(): void {
    if (this.zonaCiudad && this.zonaCiudad.zona) {
      this.loading = true;
      this.zonaCiudadService.getZonaCiudadForEdit(this.zonaCiudad.zona).subscribe({
        next: (response) => {
          if (response.success && response.data.length > 0) {
            const zona = response.data[0];
            this.form.patchValue({
              zona: zona.zona,
              descripcion: zona.descripcion
            });
            this.form.get('zona')?.disable();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo cargar la zona/ciudad'
            });
          }
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar la zona/ciudad'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else if (this.zonaCiudad) {
      this.form.patchValue({
        zona: this.zonaCiudad.zona,
        descripcion: this.zonaCiudad.descripcion
      });
      this.form.get('zona')?.disable();
    }
  }

  create(): void {
    if (this.form.valid) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      const payload = {
        que: 1,
        tope: '',
        cmd: '',
        zona: formValue.zona,
        descripcion: formValue.descripcion
      };
      this.zonaCiudadService.saveZonaCiudad(payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({ success: true, message: 'Zona/ciudad creada correctamente' });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.data?.error || 'Error al crear la zona/ciudad'
            });
          }
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear la zona/ciudad'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  update(): void {
    if (this.form.valid && this.zonaCiudad) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      const payload = {
        zona: formValue.zona,
        descripcion: formValue.descripcion
      };
      this.zonaCiudadService.updateZonaCiudad(payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.onSave.emit({ success: true, message: 'Zona/ciudad actualizada correctamente' });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo actualizar la zona/ciudad'
            });
          }
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar la zona/ciudad'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'zona',
        sClave: this.zonaCiudad?.zona || '',
        sNombre: this.form.get('descripcion')?.value,
        sTipo: 'Opcion'
      }
    });
  }

  openCiudades(): void {
    this.selectedZona = this.form.get('zona')?.value || this.zonaCiudad?.zona || null;
    this.displayCiudadesDialog = true;
  }

  onCiudadesDialogClosed(event: { refresh: boolean }): void {
    this.displayCiudadesDialog = false;
    if (event.refresh) {
      // Si quieres refrescar datos de la zona/ciudad aquí, hazlo
    }
  }
} 